import { Dispatch, RefObject, SetStateAction, useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { Message, Thought, ThoughtReply, Screen } from '../types';
import { API_BASE } from '../lib/config';
import { playChatConnected, playMessageReceived } from '../lib/sounds';
import { announceMatch, announceMessage } from '../lib/attention';
import { readAloud } from '../lib/a11y';
import { withDemo } from '../lib/skyDemo';
import { waitingPosts } from '../lib/myPosts';

interface UseSocketOptions {
  matchingRoleRef: RefObject<'VENT' | 'LISTEN' | null>;
  matchingTimeoutRef: RefObject<ReturnType<typeof setTimeout> | null>;
  identityRef: RefObject<string>;
  roomIdRef: RefObject<string | null>;
  setOnlineUsers: (count: number) => void;
  setRoomId: (id: string | null) => void;
  setOtherName: (name: string) => void;
  setIsAiChat: (val: boolean) => void;
  setMessages: Dispatch<SetStateAction<Message[]>>;
  setScreen: (screen: Screen) => void;
  setIsTyping: (typing: boolean) => void;
  setUniverseThoughts: Dispatch<SetStateAction<Thought[]>>;
  setIsConnected?: (v: boolean) => void;
  setSocketId?: (id: string | null) => void;
  setConnectionError?: (msg: string | null) => void;
  setIdentity?: (name: string) => void;
  setPartnerRole?: (role: 'VENT' | 'LISTEN') => void;
  setMatchingRole?: (role: 'VENT' | 'LISTEN') => void;
  // "Reach out" from a thought in the Sky
  setReachableThoughts?: (ids: Set<string>) => void;
  setIncomingRequest?: (req: IncomingChatRequest | null | ((prev: IncomingChatRequest | null) => IncomingChatRequest | null)) => void;
  onReachOutResult?: (requestId: string) => void;
  onMatched?: (match: MatchInfo) => void;
  // Someone you talked with said they felt heard, lighter, comforted or glad.
  onFeelingReceived?: (feeling: string) => void;
  setPartnerRecording?: (v: boolean) => void;
  // True while reconnecting to a chat (e.g. during a deploy); sending waits.
  setRejoining?: (v: boolean) => void;
  // Connection trouble in a chat with a person: this device lost its connection
  // (and later got its place back), or the other person did (and came back).
  onConnectionLost?: () => void;
  onRejoined?: () => void;
  onPartnerAway?: () => void;
  onPartnerBack?: () => void;
  // An edit could not be sent before the chat ended.
  onEditFailed?: () => void;
  // A chat restored after the page reloaded (the phone paused or reloaded the tab).
  // It rejoins on the first connect; messages already shown are not shown twice.
  // `resend`: text messages that were still on their way (the server drops repeats).
  initialChat?: { partnerName: string; messageIds: string[]; resend: ChatPayload[] } | null;
}

// One message on its way to the other person.
export interface ChatPayload {
  id: string;
  roomId: string;
  type: 'text' | 'image' | 'audio';
  text?: string;
  url?: string;
  // Voice notes: length in seconds, measured by the sender.
  duration?: number;
  authorName: string;
  // The quoted message; fromSender = the person sending this reply wrote it.
  replyTo?: { id: string; text?: string; type: 'text' | 'image' | 'audio'; fromSender: boolean };
}
// Under the server's 8 MB limit for a photo or voice note (and the 10 MB socket limit).
const MAX_ATTACHMENT_CHARS = 7_900_000;
const ACK_TIMEOUT_MS = 8000;
const MAX_TRIES = 12;

// A new chat with a person. `via: 'meet'` is a Meet Again reunion (with its pair).
export interface MatchInfo {
  roomId: string;
  partnerName: string;
  yourRole?: 'VENT' | 'LISTEN';
  via?: string;
  pairId?: string;
}

export interface IncomingChatRequest {
  requestId: string;
  thoughtId: string;
  emoji?: string;
  preview: string;
  expiresIn: number;
  receivedAt: number;
}

// A random key for this tab only. It ties presence to this anonymous session
// (it survives a reconnect, and ends when the tab closes). Never linked to a person.
const getSessionKey = () => {
  try {
    let key = sessionStorage.getItem('zuzu_session_key');
    if (!key) {
      key = (crypto.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`);
      sessionStorage.setItem('zuzu_session_key', key);
    }
    return key;
  } catch {
    return undefined;
  }
};

// A random key for this browser (all its tabs). The server uses it only to keep two tabs
// of the same browser from being matched with each other. Never linked to a person.
const getBrowserKey = () => {
  try {
    let key = localStorage.getItem('zuzu_browser_key');
    if (!key) {
      key = (crypto.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`);
      localStorage.setItem('zuzu_browser_key', key);
    }
    return key;
  } catch {
    return undefined;
  }
};

export function useSocket(options: UseSocketOptions) {
  const {
    matchingRoleRef,
    matchingTimeoutRef,
    identityRef,
    roomIdRef,
    setOnlineUsers,
    setRoomId,
    setOtherName,
    setIsAiChat,
    setMessages,
    setScreen,
    setIsTyping,
    setUniverseThoughts,
    setIsConnected,
    setSocketId,
    setConnectionError,
    setIdentity,
    setPartnerRole,
    setMatchingRole,
    setReachableThoughts,
    setIncomingRequest,
    onReachOutResult,
    onMatched,
    onFeelingReceived,
    setPartnerRecording,
    setRejoining,
    onConnectionLost,
    onRejoined,
    onPartnerAway,
    onPartnerBack,
    onEditFailed,
    initialChat,
  } = options;

  const socketRef = useRef<Socket | null>(null);
  const partnerNameRef = useRef<string>(initialChat?.partnerName ?? ''); // to find the same chat again after a server restart

  // --- Outbox: every message waits here until the server confirms it has it. ---
  // Sending only happens while connected and in the chat, so nothing is emitted
  // into a dead connection or before a rejoin. After a reconnect the rest goes out.
  // Keyed by message id for a new message, or `edit:<id>` for an edit of one.
  type Outgoing = { event: 'send_message' | 'edit_message'; msgId: string; payload: { roomId: string } & Record<string, unknown>; tries: number; inFlight: boolean; timer?: ReturnType<typeof setTimeout> };
  const outboxRef = useRef(new Map<string, Outgoing>());
  const readyRef = useRef(false); // connected and in the chat (not mid-rejoin)
  const receivedRef = useRef(new Set<string>(initialChat?.messageIds ?? [])); // ids already shown, so a resend is not shown twice
  const pendingLeaveRef = useRef<string | null>(null); // a chat ended while offline
  // A message stays "Sending…" until the other person's browser confirms it, not
  // just the server: the server may only be holding it while they are away.
  const awaitingRef = useRef(new Set<string>()); // with the server, not yet with them
  const deliveredRef = useRef(new Set<string>()); // confirmed by their browser

  const setStatus = (id: string, status: 'sending' | 'failed' | undefined) =>
    setMessages(prev => prev.map(m => (m.id === id ? { ...m, status } : m)));

  const fail = (key: string) => {
    const item = outboxRef.current.get(key);
    if (item?.timer) clearTimeout(item.timer);
    outboxRef.current.delete(key);
    if (!item) return;
    // A message that never arrived says so. A failed edit leaves the message as
    // the other person last saw it (they got the original), and says so once.
    if (item.event === 'edit_message') { setStatus(item.msgId, undefined); onEditFailed?.(); }
    else setStatus(item.msgId, 'failed');
  };
  // The chat ended: whatever did not arrive is marked "Not delivered".
  const failPending = () => {
    for (const id of [...outboxRef.current.keys()]) fail(id);
    for (const id of awaitingRef.current) setStatus(id, 'failed');
    awaitingRef.current.clear();
  };

  const trySend = (id: string) => {
    const item = outboxRef.current.get(id);
    const socket = socketRef.current;
    if (!item || item.inFlight || !socket?.connected || !readyRef.current) return;
    if (item.payload.roomId !== roomIdRef.current) return fail(id);
    item.inFlight = true;
    socket.timeout(ACK_TIMEOUT_MS).emit(item.event, item.payload, (err: Error | null, res?: { ok: boolean; retry?: boolean; retryAfter?: number }) => {
      item.inFlight = false;
      if (!outboxRef.current.has(id)) return;
      if (!err && res?.ok) {
        outboxRef.current.delete(id);
        // An edit is done here. A message waits for the other person's browser.
        if (item.event === 'send_message' && !deliveredRef.current.has(item.msgId)) awaitingRef.current.add(item.msgId);
        else setStatus(item.msgId, undefined);
        return;
      }
      if (!err && res && !res.retry) return fail(id);
      item.tries += 1;
      if (item.tries > MAX_TRIES) return fail(id);
      const wait = res?.retryAfter ?? Math.min(1000 * 2 ** Math.min(item.tries, 4), 10000);
      if (item.timer) clearTimeout(item.timer);
      item.timer = setTimeout(() => trySend(id), wait);
    });
  };
  const flush = () => { for (const id of [...outboxRef.current.keys()]) trySend(id); };

  // Send a message to the other person (text, GIF, photo or voice note).
  const sendChat = (payload: ChatPayload) => {
    if ((payload.url?.length ?? 0) > MAX_ATTACHMENT_CHARS) { setStatus(payload.id, 'failed'); return false; }
    outboxRef.current.set(payload.id, { event: 'send_message', msgId: payload.id, payload: { ...payload }, tries: 0, inFlight: false });
    setStatus(payload.id, 'sending');
    trySend(payload.id);
    return true;
  };

  // Change the text of your own message. Returns 'queued' when the original has
  // not gone out yet (its text is simply replaced, so it is not "edited").
  const editChat = (edit: { id: string; roomId: string; text: string }): 'queued' | 'sent' => {
    const waiting = outboxRef.current.get(edit.id);
    if (waiting && waiting.event === 'send_message' && !waiting.inFlight) {
      waiting.payload = { ...waiting.payload, text: edit.text };
      return 'queued';
    }
    const key = `edit:${edit.id}`;
    const old = outboxRef.current.get(key);
    if (old?.timer) clearTimeout(old.timer);
    outboxRef.current.set(key, { event: 'edit_message', msgId: edit.id, payload: { roomId: edit.roomId, messageId: edit.id, text: edit.text }, tries: 0, inFlight: false });
    setStatus(edit.id, 'sending');
    trySend(key);
    return 'sent';
  };

  // End a chat. Offline? It is told to the server as soon as the connection is back.
  const endChat = (roomId: string) => {
    failPending();
    if (socketRef.current?.connected) socketRef.current.emit('leave_chat', { roomId });
    else pendingLeaveRef.current = roomId;
  };

  useEffect(() => {
    // Guard against React StrictMode's double-invoke: reuse existing live socket
    if (socketRef.current?.connected) return;

    // Restored after a reload: still-sending texts wait in the outbox for the rejoin.
    for (const p of initialChat?.resend ?? []) {
      if (!outboxRef.current.has(p.id)) outboxRef.current.set(p.id, { event: 'send_message', msgId: p.id, payload: { ...p }, tries: 0, inFlight: false });
    }

    // In production, VITE_API_URL points to the Railway backend.
    // Empty string means same-origin (local dev where server.ts serves everything).
    const backendUrl = API_BASE;

    const socket = io(backendUrl, {
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      auth: { ownerToken: localStorage.getItem('zuzu_owner_token') || '', sessionKey: getSessionKey(), browserKey: getBrowserKey() },
    });
    socketRef.current = socket;

    const requestThoughts = () => socket.emit('get_initial_thoughts');
    let isInitialConnect = true;
    socket.on('connect_error', (err) => {
      console.error('[Zuzu] connect_error:', err.message, err);
      setConnectionError?.(err.message || 'Unknown error');
    });

    socket.on('connect', () => {
      setConnectionError?.(null);
      setIsConnected?.(true);
      setSocketId?.(socket.id ?? null);
      requestThoughts();
      socket.emit('claim_name', identityRef.current, (assigned: string) => {
        if (assigned && assigned !== identityRef.current) {
          identityRef.current = assigned;
          setIdentity?.(assigned);
          localStorage.setItem('zuzu_identity', assigned);
          localStorage.setItem('zuzu_identity_time', Date.now().toString());
        }
      });
      if (pendingLeaveRef.current) {
        socket.emit('leave_chat', { roomId: pendingLeaveRef.current });
        pendingLeaveRef.current = null;
      }
      // In a chat (also right after a reload restored it): ask for its place back.
      if (roomIdRef.current) {
        readyRef.current = false; // wait for 'rejoined' before sending
        setRejoining?.(true);
        socket.emit('rejoin_room', { roomId: roomIdRef.current, name: identityRef.current, partnerName: partnerNameRef.current });
      } else if (!isInitialConnect && matchingRoleRef.current) {
        socket.emit('start_matching', { role: matchingRoleRef.current, name: identityRef.current });
      }
      isInitialConnect = false;
    });

    socket.on('disconnect', () => {
      setIsConnected?.(false);
      setSocketId?.(null);
      readyRef.current = false;
      if (roomIdRef.current) onConnectionLost?.();
    });

    socket.on('user_count', (count: number) => {
      setOnlineUsers(count);
    });

    socket.on('matched', (data: MatchInfo & { partnerId: string; partnerRole?: 'VENT' | 'LISTEN' }) => {
      console.log('[Zuzu] matched event received', data, 'role:', matchingRoleRef.current);
      if (matchingTimeoutRef.current) {
        clearTimeout(matchingTimeoutRef.current);
        matchingTimeoutRef.current = null;
      }
      playChatConnected();
      setRoomId(data.roomId);
      setOtherName(data.partnerName);
      partnerNameRef.current = data.partnerName;
      if (data.partnerRole) setPartnerRole?.(data.partnerRole);
      setIsAiChat(false);
      failPending(); // anything left from a previous chat
      deliveredRef.current = new Set();
      setRejoining?.(false);
      receivedRef.current = new Set();
      readyRef.current = true;
      setMessages([]);
      // A chat that started from a thought tells each side its role.
      if (data.yourRole) {
        matchingRoleRef.current = data.yourRole;
        setMatchingRole?.(data.yourRole);
      }
      setIncomingRequest?.(null);
      setPartnerRecording?.(false);
      onMatched?.(data);
      const currentRole = matchingRoleRef.current || 'VENT';
      setScreen(currentRole);
      // Waiting in another tab? Let them know (title, and a notification if allowed).
      announceMatch(currentRole === 'VENT' ? 'A kind listener just joined. Come say hi.' : 'Someone who needs to be heard just joined.');
    });

    // Confirm each message to the server, so it can hold and resend it if this
    // device was unreachable. A resend of a message already shown is ignored.
    socket.on('receive_message', (msg: Omit<Message, 'replyTo'> & { replyTo?: { id: string; text?: string; type: 'text' | 'image' | 'audio'; fromSender: boolean } }, ack?: () => void) => {
      if (typeof ack === 'function') ack();
      if (!msg?.id || receivedRef.current.has(msg.id)) return;
      receivedRef.current.add(msg.id);
      // The quote as seen from here: what the sender wrote is "theirs" on this side.
      const { replyTo, ...rest } = msg;
      const quote = replyTo ? { id: replyTo.id, text: replyTo.text, type: replyTo.type, mine: !replyTo.fromSender } : undefined;
      setMessages((prev) => [...prev, { ...rest, replyTo: quote, timestamp: Date.now() }]);
      if (rest.type === 'text' && rest.text) readAloud(rest.text);
      announceMessage();
      setIsTyping(false);
      playMessageReceived();
    });

    // The other person opened one of your photos (tapped "Tap to view").
    socket.on('photo_viewed', ({ messageId }: { messageId: string }, ack?: () => void) => {
      if (typeof ack === 'function') ack();
      if (typeof messageId !== 'string') return;
      setMessages(prev => prev.map(m => (m.id === messageId && m.sender === 'user' && m.type === 'image' ? { ...m, viewed: true } : m)));
    });

    // The other person edited one of their messages.
    socket.on('message_edited', ({ messageId, text }: { messageId: string; text: string }, ack?: () => void) => {
      if (typeof ack === 'function') ack();
      if (typeof messageId !== 'string' || typeof text !== 'string') return;
      setMessages(prev => prev.map(m => (m.id === messageId && m.sender === 'other' ? { ...m, text, edited: true } : m)));
    });

    // These messages reached the other person's browser.
    socket.on('messages_delivered', ({ ids }: { ids: string[] }) => {
      if (!Array.isArray(ids)) return;
      const arrived = new Set(ids.filter(id => typeof id === 'string'));
      for (const id of arrived) { deliveredRef.current.add(id); awaitingRef.current.delete(id); }
      setMessages(prev => prev.map(m => (arrived.has(m.id) && m.sender === 'user' && m.status ? { ...m, status: undefined } : m)));
    });

    socket.on('feeling_received', ({ feeling }: { feeling: string }) => {
      onFeelingReceived?.(feeling);
    });

    // The other person reacted to a message (or took it back).
    socket.on('partner_reaction', ({ messageId, emoji }: { messageId: string; emoji: string | null }, ack?: () => void) => {
      if (typeof ack === 'function') ack();
      setMessages((prev) => prev.map(m => m.id === messageId
        ? { ...m, reactions: { ...m.reactions, theirs: emoji ?? undefined } }
        : m));
    });

    socket.on('partner_typing', (typing: boolean) => {
      setIsTyping(typing);
    });

    // The other person is recording a voice note.
    socket.on('partner_recording', (recording: boolean) => {
      setPartnerRecording?.(recording);
    });

    socket.on('initial_thoughts', (thoughts: Thought[]) => {
      // Plus my own thoughts still waiting for a check (only I see them), and
      // made-up thoughts only with ?demo on the dev server.
      const ids = new Set(thoughts.map(t => t.id));
      setUniverseThoughts(withDemo([...thoughts, ...waitingPosts().filter(t => !ids.has(t.id))]));
    });

    const onVisible = () => {
      socket.emit('presence', { visible: document.visibilityState === 'visible' });
      if (document.visibilityState !== 'visible') return;
      if (socket.connected) {
        socket.emit('get_initial_thoughts');
      } else {
        // Coming back to the foreground with a dead/zombie socket (common on iOS
        // Safari after backgrounding). Force an immediate reconnect instead of
        // waiting for the backoff timer, so the user rejoins their chat fast.
        socket.connect();
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    // The phone lost its network: say so at once instead of waiting for the
    // heartbeat. Closing the transport makes socket.io reconnect by itself.
    const onOffline = () => { if (socket.connected) socket.io.engine?.close(); };
    const onOnline = () => { if (!socket.connected) socket.connect(); };
    // Back from the browser's page cache (iOS swipe back): reconnect right away.
    const onPageShow = (e: PageTransitionEvent) => { if (e.persisted && !socket.connected) socket.connect(); };
    window.addEventListener('offline', onOffline);
    window.addEventListener('online', onOnline);
    window.addEventListener('pageshow', onPageShow);

    socket.on('receive_thought', (thought: Thought) => {
      // My own waiting thought, now approved, takes the place of my copy.
      setUniverseThoughts((prev) => prev.some(t => t.id === thought.id) ? prev.map(t => (t.id === thought.id ? thought : t)) : [...prev, thought]);
      window.dispatchEvent(new CustomEvent('zuzu:add_thought', { detail: thought }));
    });

    socket.on('remove_thought', ({ id }: { id: string }) => {
      setUniverseThoughts((prev) => prev.filter(t => t.id !== id));
      window.dispatchEvent(new CustomEvent('zuzu:remove_thought', { detail: { id } }));
    });

    // A reply was approved in the dashboard, or removed.
    socket.on('thought_reply', ({ thoughtId, reply }: { thoughtId: string; reply: ThoughtReply }) => {
      setUniverseThoughts((prev) => prev.map(t => t.id === thoughtId && !(t.replies ?? []).some(r => r.id === reply.id)
        ? { ...t, replies: [...(t.replies ?? []), reply] }
        : t));
    });

    // A thought's feeling was set (AI guess for a dashboard thought, or the writer changed it).
    socket.on('thought_updated', ({ id, feelings }: { id: string; feelings?: string[] }) => {
      setUniverseThoughts((prev) => prev.map(t => (t.id === id ? { ...t, feelings } : t)));
    });

    // Zuzu's guess for the thought this person just posted.
    socket.on('thought_feeling', ({ thoughtId, feelings }: { thoughtId: string; feelings: string[] }) => {
      // Keep the writer's own pick if they tapped before the guess arrived.
      setUniverseThoughts((prev) => prev.map(t => (t.id === thoughtId && !t.feelings?.length ? { ...t, feelings } : t)));
    });

    // One of my posts is waiting for a person's check (App shows it).
    socket.on('post_status', (detail: { kind: 'thought'; id: string; status: 'waiting'; fix?: string }) => {
      window.dispatchEvent(new CustomEvent('zuzu:post_status', { detail }));
    });

    // The thought's writer loved a reply (or took it back).
    socket.on('reply_loved', ({ thoughtId, replyId, loved }: { thoughtId: string; replyId: string; loved: boolean }) => {
      setUniverseThoughts((prev) => prev.map(t => t.id === thoughtId
        ? { ...t, replies: (t.replies ?? []).map(r => (r.id === replyId ? { ...r, loved } : r)) }
        : t));
    });

    socket.on('thought_me_too', ({ id, count }: { id: string; count: number }) => {
      setUniverseThoughts((prev) => prev.map(t => (t.id === id ? { ...t, meToo: count } : t)));
    });

    // A reply's text was fixed in the dashboard.
    socket.on('reply_edited', ({ thoughtId, replyId, text }: { thoughtId: string; replyId: string; text: string }) => {
      setUniverseThoughts((prev) => prev.map(t => t.id === thoughtId
        ? { ...t, replies: (t.replies ?? []).map(r => (r.id === replyId ? { ...r, text } : r)) }
        : t));
    });

    socket.on('remove_reply', ({ thoughtId, replyId }: { thoughtId: string; replyId: string }) => {
      setUniverseThoughts((prev) => prev.map(t => t.id === thoughtId
        ? { ...t, replies: (t.replies ?? []).filter(r => r.id !== replyId) }
        : t));
    });

    // Which recent thoughts have their writer here right now (ids only).
    socket.on('thought_presence', (ids: string[]) => {
      setReachableThoughts?.(new Set(Array.isArray(ids) ? ids : []));
    });

    // Someone would like to talk to this person about their thought.
    socket.on('chat_request', (req: Omit<IncomingChatRequest, 'receivedAt'>) => {
      setIncomingRequest?.({ ...req, receivedAt: Date.now() });
    });
    socket.on('chat_request_closed', ({ requestId }: { requestId: string }) => {
      setIncomingRequest?.(prev => (prev?.requestId === requestId ? null : prev));
    });
    socket.on('reach_out_result', ({ requestId }: { requestId: string }) => {
      onReachOutResult?.(requestId);
    });

    socket.on('queue_updated', () => {
      window.dispatchEvent(new CustomEvent('zuzu:queue_updated'));
    });

    socket.on('partner_away', () => onPartnerAway?.());

    socket.on('partner_reconnected', () => {
      setRejoining?.(false);
      readyRef.current = true;
      onPartnerBack?.();
      flush();
      setMessages((prev) => [
        ...prev,
        {
          id: 'system-' + Date.now(),
          text: 'Your partner is back.',
          type: 'text',
          sender: 'other',
          authorName: 'System',
        },
      ]);
    });

    // The other person wrote something that sounds heavy. Show a gentle tip, once.
    socket.on('partner_needs_care', () => {
      setMessages((prev) => prev.some(m => m.kind === 'care-listener') ? prev : [
        ...prev,
        { id: 'care-' + Date.now(), type: 'text', sender: 'other', authorName: 'System', kind: 'care-listener' },
      ]);
    });

    const note = (text: string) => setMessages((prev) => [
      ...prev,
        { id: 'system-' + Date.now(), type: 'text', sender: 'other', authorName: 'System', text },
    ]);

    // Zuzu is about to restart (a deploy): let the chat know, gently.
    socket.on('server_restarting', () => {
      if (roomIdRef.current) note('Zuzu is updating. Reconnecting you in a moment…');
    });
    // Back on the server, waiting for the other person to reconnect too.
    socket.on('rejoin_waiting', () => note('Reconnecting you to your chat…'));
    socket.on('rejoined', () => {
      setRejoining?.(false);
      readyRef.current = true;
      onRejoined?.();
      flush();
    });

    // The chat could not be restored: stay on the chat, say so kindly.
    socket.on('rejoin_failed', () => {
      setRejoining?.(false);
      failPending();
      if (roomIdRef.current) note("This chat couldn't be restored. You can start a new one anytime.");
      setRoomId(null);
    });

    socket.on('partner_disconnected', () => {
      setPartnerRecording?.(false);
      setRejoining?.(false);
      failPending();
      setMessages((prev) => [
        ...prev,
        {
          id: 'system-' + Date.now(),
          text: 'Your partner has disconnected.',
          type: 'text',
          sender: 'other',
          authorName: 'System',
        },
      ]);
      setRoomId(null);
    });

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('pageshow', onPageShow);
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { socketRef, sendChat, editChat, endChat, failPending };
}
